import { FC, MouseEvent, useCallback, useEffect, useState } from 'react'
import { useTranslation } from '../../../../hooks/locale'
import { useBulkInquiry } from './use-bulk-inquiry'
import { CookieManagerService } from '../../../../services'
import AnalyticsService from '../../../../services/analytics-service'
import { bulkInquiryPageCategories } from '../../../../services/analytics-service/categories'
import { bulkInquiryPageActions } from '../../../../services/analytics-service/actions'
import {
    DesktopToggleContainer,
    MobileToggleContainer,
    CheckboxControl,
    ToggleCheckboxInput,
    ToggleCheckboxBox,
    ToggleText,
    NewBadge,
    TooltipTrigger,
    TooltipCloseButton,
    TooltipCloseIcon,
    Tooltip,
} from './bulk-inquiry-toggle-styles'

interface Props {
    variant: 'desktop' | 'mobile'
}

const BulkInquiryToggle: FC<Props> = ({ variant }) => {
    const { t } = useTranslation()
    const { isEnabled, isSelectMode, toggleSelectMode } = useBulkInquiry()
    // Defaults to "already seen" so SSR/first paint never shows it, then flips once the cookie's
    // (client-only) absence is confirmed — the brief post-hydration appearance is the intended
    // first-time-ever behaviour, not a flash of the wrong state.
    const [hasSeenTooltip, setHasSeenTooltip] = useState(true)

    useEffect(() => {
        if (!CookieManagerService.getCookie(CookieManagerService.BULK_INQUIRY_TOGGLE_TOOLTIP_SEEN)) {
            setHasSeenTooltip(false)
        }
    }, [])

    // Fires on every hover-in, not just the first — each visit to the tooltip counts.
    const handleTooltipTriggerHover = useCallback(() => {
        AnalyticsService.trackEvent({
            category: bulkInquiryPageCategories.SRL,
            action: bulkInquiryPageActions.INFO_ICON_HOVERED,
        })
    }, [])

    // Only the explicit close (X) dismisses the auto-shown tooltip and remembers it — hovering,
    // clicking the badge itself, or toggling the checkbox must not silently mark it as seen.
    const handleDismissAutoTooltip = useCallback((event: MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
        AnalyticsService.trackEvent({
            category: bulkInquiryPageCategories.SRL,
            action: bulkInquiryPageActions.TOGGLE_TOOLTIP_DISMISSED,
        })
        CookieManagerService.setCookie(CookieManagerService.BULK_INQUIRY_TOGGLE_TOOLTIP_SEEN, '1', { expires: 365 })
        setHasSeenTooltip(true)
    }, [])

    // The mobile container is a <label> spanning the whole row, so the trigger button needs to stop
    // its click from bubbling — otherwise tapping the badge would also toggle the checkbox beneath it.
    const handleTriggerClick = useCallback((event: MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
    }, [])

    if (!isEnabled) {
        return null
    }

    const inputId = `bulk-inquiry-toggle-${variant}`
    const label = t('bulkInquiry.selectMultipleToggle')
    const tooltipText = t('bulkInquiry.toggleTooltip')

    const tooltipTrigger = (
        <TooltipTrigger
            tabIndex={0}
            aria-label={tooltipText}
            onMouseEnter={handleTooltipTriggerHover}
            onFocus={handleTooltipTriggerHover}
            onClick={handleTriggerClick}
        >
            <NewBadge>{t('new')}</NewBadge>
            <Tooltip role="tooltip" $forceVisible={!hasSeenTooltip}>
                {tooltipText}
                {!hasSeenTooltip && (
                    <TooltipCloseButton
                        type="button"
                        aria-label={t('cancelIconText')}
                        onClick={handleDismissAutoTooltip}
                    >
                        <TooltipCloseIcon />
                    </TooltipCloseButton>
                )}
            </Tooltip>
        </TooltipTrigger>
    )

    if (variant === 'mobile') {
        return (
            <MobileToggleContainer htmlFor={inputId}>
                {tooltipTrigger}
                <ToggleText>{label}</ToggleText>
                <ToggleCheckboxInput
                    id={inputId}
                    type="checkbox"
                    aria-label={label}
                    checked={isSelectMode}
                    onChange={toggleSelectMode}
                />
                <ToggleCheckboxBox />
            </MobileToggleContainer>
        )
    }

    return (
        <DesktopToggleContainer>
            {tooltipTrigger}
            <ToggleText>{label}</ToggleText>
            <CheckboxControl htmlFor={inputId}>
                <ToggleCheckboxInput
                    id={inputId}
                    type="checkbox"
                    aria-label={label}
                    checked={isSelectMode}
                    onChange={toggleSelectMode}
                />
                <ToggleCheckboxBox />
            </CheckboxControl>
        </DesktopToggleContainer>
    )
}

export default BulkInquiryToggle
